<template>
    <div class="evaluation-calendar">
        <!-- Calendar Date Picker -->
        <div class="calendar-section">
            <div class="section-label">Select a date</div>
            <v-date-picker
                v-model="selectedDate"
                :min="minDate"
                :allowed-dates="isDateAvailable"
                color="#D4780A"
                full-width
                no-title
                :loading="loadingDates"
                @update:picker-date="onMonthChange"
                class="evaluation-datepicker"
            />
        </div>

        <!-- Time Slots Grid -->
        <div v-if="selectedDate" class="timeslots-section">
            <div class="section-label">
                Available times
                <span class="timezone-hint">({{ userTimezoneLabel }})</span>
            </div>

            <div v-if="loadingSlots" class="slots-loading">
                <v-progress-circular indeterminate color="#D4780A" size="32" />
                <span>Loading available times...</span>
            </div>

            <div v-else-if="availableSlots.length === 0" class="no-slots">
                <v-icon color="#9E9E9E" size="40">mdi-calendar-remove</v-icon>
                <p>No available time slots for this date.</p>
                <p class="no-slots-hint">Try selecting a different date.</p>
            </div>

            <div v-else class="slots-grid">
                <button
                    v-for="slot in availableSlots"
                    :key="slot.value"
                    type="button"
                    class="slot-chip"
                    :class="{ 'selected': selectedSlot === slot.value }"
                    @click="selectSlot(slot)"
                >
                    {{ slot.label }}
                </button>
            </div>
        </div>

        <!-- WhatsApp Fallback -->
        <div v-if="whatsappNumber" class="whatsapp-fallback">
            <div class="fallback-divider"></div>
            <p class="fallback-text">Can't find a suitable time?</p>
            <a :href="`https://wa.me/${whatsappNumber}`" target="_blank" class="whatsapp-link">
                <v-icon size="20" color="#25D366">mdi-whatsapp</v-icon>
                Contact us on WhatsApp
            </a>
        </div>
    </div>
</template>

<script>
import moment from 'moment-timezone';

export default {
    name: 'EvaluationCalendar',
    props: {
        // If set, the component is in "selection mode" (wizard) — emits selection, no booking
        selectionOnly: {
            type: Boolean,
            default: false
        },
        // Pre-selected slot value (for wizard sync)
        value: {
            type: String,
            default: ''
        },
        whatsappNumber: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            selectedDate: null,
            selectedSlot: this.value || null,
            availableDates: [],
            availableSlots: [],
            loadingDates: false,
            loadingSlots: false,
            currentPickerMonth: moment().format('YYYY-MM'),
        };
    },
    computed: {
        minDate() {
            return moment().format('YYYY-MM-DD');
        },
        userTimezone() {
            return moment.tz.guess();
        },
        userTimezoneLabel() {
            const tz = this.userTimezone;
            const abbr = moment.tz(tz).format('z');
            // Show abbreviation if meaningful, otherwise show offset
            if (abbr && !abbr.startsWith('+') && !abbr.startsWith('-')) {
                return abbr;
            }
            return 'UTC' + moment.tz(tz).format('Z');
        },
    },
    watch: {
        selectedDate(date) {
            if (date) {
                this.fetchAvailableSlots(date);
                this.selectedSlot = null;
                this.$emit('input', '');
            }
        },
        value(val) {
            this.selectedSlot = val;
        }
    },
    methods: {
        async fetchAvailableDates(month) {
            this.loadingDates = true;
            try {
                const response = await window.axios.get('/evaluations/available-dates', {
                    params: { month }
                });
                this.availableDates = response.data.dates || [];
            } catch (error) {
                console.error('Failed to fetch available dates:', error);
                this.availableDates = [];
            } finally {
                this.loadingDates = false;
            }
        },
        async fetchAvailableSlots(date) {
            this.loadingSlots = true;
            this.availableSlots = [];
            try {
                const response = await window.axios.get('/evaluations/available-slots', {
                    params: { date }
                });
                const slots = response.data.slots || [];
                // Convert UTC slots to user's timezone for display
                this.availableSlots = slots.map(slot => {
                    const utcMoment = moment.utc(slot.start);
                    const localMoment = utcMoment.clone().tz(this.userTimezone);
                    return {
                        value: slot.start, // keep UTC ISO string for sending back to server
                        label: localMoment.format('h:mm A'),
                    };
                });
            } catch (error) {
                console.error('Failed to fetch available slots:', error);
                this.availableSlots = [];
            } finally {
                this.loadingSlots = false;
            }
        },
        isDateAvailable(date) {
            return this.availableDates.includes(date);
        },
        onMonthChange(yearMonth) {
            if (yearMonth !== this.currentPickerMonth) {
                this.currentPickerMonth = yearMonth;
                this.fetchAvailableDates(yearMonth);
            }
        },
        selectSlot(slot) {
            this.selectedSlot = slot.value;
            this.$emit('input', slot.value);
            this.$emit('slot-selected', {
                utcValue: slot.value,
                displayLabel: slot.label,
                date: this.selectedDate,
            });
        },
    },
    mounted() {
        const currentMonth = moment().format('YYYY-MM');
        this.fetchAvailableDates(currentMonth);
    }
};
</script>

<style lang="scss" scoped>
.evaluation-calendar {
    font-family: 'Inter', sans-serif;
}

.section-label {
    font-size: 15px;
    font-weight: 600;
    color: #1E1E1E;
    margin-bottom: 12px;
    display: flex;
    align-items: baseline;
    gap: 6px;
}

.timezone-hint {
    font-size: 13px;
    font-weight: 400;
    color: #9E9E9E;
}

.calendar-section {
    margin-bottom: 28px;
}

.evaluation-datepicker {
    border: 1px solid #E0E0E0;
    border-radius: 12px;
    box-shadow: none !important;

    ::v-deep .v-date-picker-header {
        padding: 8px 16px;
    }

    ::v-deep .v-btn--active {
        background-color: #D4780A !important;
    }

    ::v-deep .v-date-picker-table {
        padding: 0 8px 8px;
    }
}

.timeslots-section {
    margin-bottom: 24px;
}

.slots-loading {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 24px 0;
    color: #707070;
    font-size: 14px;
}

.no-slots {
    text-align: center;
    padding: 32px 16px;
    color: #9E9E9E;

    p {
        margin: 8px 0 0;
        font-size: 14px;
    }

    .no-slots-hint {
        font-size: 13px;
        color: #BDBDBD;
    }
}

.slots-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
}

.slot-chip {
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #1E1E1E;
    background: #FFFFFF;
    border: 1.5px solid #E0E0E0;
    border-radius: 10px;
    padding: 10px 18px;
    cursor: pointer;
    transition: all 0.15s ease;
    outline: none;

    &:hover {
        border-color: #D4780A;
        background: #FFF8F0;
    }

    &.selected {
        border-color: #D4780A;
        background: #D4780A;
        color: #FFFFFF;
    }
}

.whatsapp-fallback {
    margin-top: 24px;
    text-align: center;
}

.fallback-divider {
    height: 1px;
    background: #E8E8E8;
    margin-bottom: 20px;
}

.fallback-text {
    font-size: 14px;
    color: #9E9E9E;
    margin-bottom: 8px;
}

.whatsapp-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    font-weight: 500;
    color: #25D366;
    text-decoration: none;
    transition: opacity 0.2s ease;

    &:hover {
        opacity: 0.8;
    }
}

@media (max-width: 600px) {
    .slot-chip {
        padding: 10px 14px;
        font-size: 13px;
    }

    .slots-grid {
        gap: 8px;
    }
}
</style>
